import React, { useContext, useEffect, useMemo } from "react";
import { ChevronRight, Edit, Settings, Sync } from "@mui/icons-material";
import { Alert, Box, IconButton, Tooltip, Typography } from "@mui/material";

import { NavigationButton } from "ui";

import { LayoutContext } from "fond/layout/LayoutProvider";
import { SubLayoutPopout } from "fond/layout/SubLayoutPopout";
import { openArchitectureModal } from "fond/project/redux";
import { BaseDataFlyoutTarget, closeBaseDataFlyout, openBaseDataFlyout, selectBaseDataFlyoutTarget } from "fond/redux/baseDataFlyout";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

import BaseDataFlyout from "./BaseDataFlyout";
import BaseDataRow from "./BaseDataRow";
import { BASE_DATA_GROUPS } from "./layers";
import ModifyDataFlyout from "./ModifyDataFlyout";
import { BaseDataModel, BaseDataModelContext } from "./useBaseDataModel";

const FLYOUT_RECT = { x: 4, y: 38, width: 380 };
const FLYOUT_BOTTOM_MARGIN = 8;

interface BaseDataPanelProps {
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
  model: BaseDataModel;
}

/** Return the height that makes the flyout fill the map sub-layout, or undefined to fit its content. */
const useFlyoutHeight = (open: boolean): number | undefined => {
  const layout = useContext(LayoutContext);
  return useMemo(() => {
    if (!open) return undefined;
    const mapHeight = layout?.mapModel?.getRootRow().getRect().height ?? 0;
    const height = mapHeight - FLYOUT_RECT.y - FLYOUT_BOTTOM_MARGIN;
    return height > 0 ? height : undefined;
  }, [open, layout?.mapModel]);
};

/** The Input Data step of a design project: base data layer rows, their generation and clean-up tools. */
const BaseDataPanel: React.FC<BaseDataPanelProps> = ({ readOnly, model }: BaseDataPanelProps) => {
  const dispatch = useAppDispatch();
  const { stack } = useStackedNavigationContext();
  const flyoutTarget = useAppSelector(selectBaseDataFlyoutTarget);
  const { rows, panel, actions } = model;

  // The flyout closes when the panel does.
  useEffect(
    () => () => {
      dispatch(closeBaseDataFlyout());
    },
    [dispatch]
  );

  const isModifyDataAvailable = panel.modifyData.available;
  // Hide the flyout while a tool is open in the left panel.
  const isFlyoutOpen = flyoutTarget !== null && stack.length === 0 && (flyoutTarget !== "modifyData" || isModifyDataAvailable);
  const flyoutHeight = useFlyoutHeight(isFlyoutOpen);

  const handleOpenSettings = () => {
    dispatch(openArchitectureModal("DesignPreferences"));
  };

  const selectFlyoutTarget = (target: BaseDataFlyoutTarget | null) => {
    dispatch(openBaseDataFlyout({ target }));
  };

  const toggleFlyout = (target: BaseDataFlyoutTarget) => {
    selectFlyoutTarget(flyoutTarget === target ? null : target);
  };

  return (
    <BaseDataModelContext.Provider value={model}>
      {BASE_DATA_GROUPS.map((group, index) => (
        // The first heading's line height already spaces it from the pane's padding.
        // Groups split their gap either side of the heading, so headings sit midway between rows.
        <Box key={group.id} mt={index === 0 ? -0.5 : 0} mb={0.5} role="group" aria-label={group.label}>
          <Box display="flex" alignItems="center" justifyContent="space-between" pb={0.5}>
            <Typography variant="overline" color="text.secondary" fontWeight={500} display="block" lineHeight={1.75} px={0.25}>
              {group.label}
            </Typography>
            {/* The architecture's design preferences shape the candidate network, so its settings live on that heading. */}
            {group.id === "candidateNetwork" && (
              <Tooltip title="Design preferences">
                <span>
                  <IconButton
                    aria-label="Design preferences"
                    size="small"
                    color="primary"
                    disabled={readOnly}
                    onClick={handleOpenSettings}
                    sx={{ p: 0.25 }}
                  >
                    <Settings fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Box display="flex" flexDirection="column">
            {group.keys.map((key) => (
              <BaseDataRow key={key} row={rows[key]} selected={flyoutTarget === key} disabled={readOnly} onClick={() => toggleFlyout(key)} />
            ))}
          </Box>
        </Box>
      ))}

      {/* While base data generates, the Modify data flyout stays viewable and its tools give the reason they're closed. */}
      <Box mb={0.5} role="group" aria-label="REFINE">
        <Typography variant="overline" color="text.secondary" fontWeight={500} display="block" lineHeight={1.75} px={0.25} pb={0.5}>
          REFINE
        </Typography>
        <NavigationButton
          icon={<Edit />}
          iconSize="small"
          title="Modify data"
          subtitle="Tidy up your base data"
          variant="outlined"
          disabled={!isModifyDataAvailable}
          onClick={() => toggleFlyout("modifyData")}
          rightElement={<ChevronRight sx={{ fontSize: 20, color: flyoutTarget === "modifyData" ? "primary.main" : "action.disabled" }} />}
          sx={{
            my: 0,
            ...(flyoutTarget === "modifyData" && { backgroundColor: "rgba(33, 150, 243, 0.05)", borderColor: "primary.main" }),
          }}
        />
      </Box>

      {panel.isStale && (
        <Alert severity="warning" variant="compact" icon={<Sync />} sx={{ mt: 1 }}>
          Your data has changed since generation. Open the out-of-date layers to generate them again.
        </Alert>
      )}

      <SubLayoutPopout tabId="base-data-flyout" open={isFlyoutOpen} rect={{ ...FLYOUT_RECT, height: flyoutHeight }} belowModals>
        {flyoutTarget === "modifyData" && isModifyDataAvailable && (
          <ModifyDataFlyout gates={panel.modifyData.gates} onClose={() => selectFlyoutTarget(null)} />
        )}
        {flyoutTarget && flyoutTarget !== "modifyData" && (
          <BaseDataFlyout key={flyoutTarget} row={rows[flyoutTarget]} actions={actions} onSelect={selectFlyoutTarget} />
        )}
      </SubLayoutPopout>
    </BaseDataModelContext.Provider>
  );
};

export default BaseDataPanel;
